import App, { FlashApp } from "../app";
import {
	AdvanceEvent, FlipCardEvent, JumpEvent, NextEvent, RandomizeEvent,
	ResetTimerEvent, TickEvent, ToggleAdvanceEvent,
} from "../events";
import { AttemptId, ItemId, ProductId, QuizId } from "../model/datasource";
import { ListItem } from "../model/datasource/list";
import Controller from "./controller";

export class CardController<P extends ProductId, A extends AttemptId, Z extends QuizId, I extends ItemId> extends Controller<P, A, Z, I> {
	bind(target: EventTarget, app: FlashApp<P, A, Z, I>): void {
		target.addEventListener(FlipCardEvent.type, (e: FlipCardEvent) => this.flip(app, e.detail));
		target.addEventListener(AdvanceEvent.type, () => this.advance(app));
		target.addEventListener(TickEvent.type, () => this.tick(app));
		target.addEventListener(ResetTimerEvent.type, () => this.reset(app));
		target.addEventListener(RandomizeEvent.type, (e: RandomizeEvent) => this.randomize(app, e.detail));
	}

	tick(app: App<P, A, Z, I>): void {
		const current: number = app.current.get();
		const total: number = app.timer.total([current]);
		app.view.header.displayCurrentTime(total);
	}

	advance(app: App<P, A, Z, I>): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		const answer_visible: boolean = app.current.getAnswerVisible();
		if (answer_visible) {
			const current = app.current.get();
			const nextTest = app.current.getAdjacent(1);
			if (nextTest === current) {
				bindTarget.dispatchEvent(new ToggleAdvanceEvent(false));
			} else {
				bindTarget.dispatchEvent(new NextEvent());
			}
		} else {
			bindTarget.dispatchEvent(new FlipCardEvent(false));
		}
	}

	flip(app: FlashApp<P, A, Z, I>, front: boolean): void {
		const current = app.current.get();
		app.timer.switch(current, current, { front });
		app.current.setAnswerVisible(!front);
		app.view.header.displayFlip(front);
		if (front) {
			app.view.item.displayCardFront();
		} else {
			app.view.item.displayCardBack();
		}
	}

	reset(app: App<P, A, Z, I>): Promise<void> {
		const bindTarget: EventTarget = app.view.getBindTarget();
		return app.model.list.getItemList().then(items => {
			const update = app.model.state.attempt.updateState({ times: { } });
			items.forEach((item: ListItem<I>) =>
				app.model.list.setAttributes(item.id, { initial: 0 })
			);
			app.timer.reset(app.current.get());
			bindTarget.dispatchEvent(new TickEvent());
			return update;
		});
	}

	randomize(app: App<P, A, Z, I>, randomize: boolean): void {
		const bindTarget: EventTarget = app.view.getBindTarget();
		const order = app.current.randomize(randomize);
		app.model.settings.setRandomize(randomize);
		app.view.header.displayRandomize(randomize);
		app.view.list.displaySorted(order);
		const current = app.current.get();
		app.model.list.getItemIndexMap().then(map => {
			Promise.all([
				app.model.state.global.updateState(
					{ settings: { randomize } }
				),
				app.model.state.attempt.updateState(
					{ current: app.current.serialize(map) }
				)
			]);
			bindTarget.dispatchEvent(new JumpEvent(
				map[current]
			));
		});
	}
}
